/**
 * Skill Detail Page Controller
 */
import { escapeHtml } from '../lib/html';
import { renderPackageReviewFileViewer } from '../lib/package-review-surface';
import { getPageUserId, publicPage, type PageAuthContext } from '../lib/page-runtime';
import {
  bindSkillDetailCopyActions,
  copyTextWithToast,
  loadSkillAnalytics,
  renderSkillMarkdownContent,
} from '../lib/skill-detail-actions';
import { renderSkillDetailPageShell } from '../lib/skill-detail-panels';
import {
  loadStarAndReviews,
  registerSkillReviewHandlers,
} from '../lib/skill-detail-reviews';
import type { SkillFile } from '../types/api';

(function() {
  'use strict';

  const root = document.getElementById('skill-detail-root');
  if (!root) return;

  const skillIdOrName = root.dataset.skillId;
  if (!skillIdOrName) {
    console.error('skill-detail: missing data-skill-id attribute');
    return;
  }

  const rootEl = root;
  const skillKey = skillIdOrName;
  let currentFiles: SkillFile[] = [];
  let currentFileIndex = 0;

  window.selectFile = function(index: number) {
    currentFileIndex = index;
    const container = document.getElementById('skill-file-explorer');
    if (container) {
      container.innerHTML = renderPackageReviewFileViewer({
        surface: 'published-skill',
        files: currentFiles,
        currentIndex: currentFileIndex,
        selectFile: 'selectFile',
        copyFileContent: 'copyFileContent',
      });
    }
  };

  window.copyFileContent = async function(index?: number) {
    const file = currentFiles[index ?? currentFileIndex];
    if (!file) return;
    await copyTextWithToast(file.content || '');
  };

  async function loadSkillDetailPage(ctx: PageAuthContext) {
    try {
      // Support both numeric IDs and string names.
      const identifier: number | string = /^\d+$/.test(skillKey)
        ? parseInt(skillKey, 10)
        : skillKey;
      const viewData = await window.SkillDetailData.loadSkillViewData(identifier);

      if (!viewData.ok) throw new Error(viewData.error);

      const { skill, files, issues, feedback, certification } = viewData.data;
      currentFiles = files;
      currentFileIndex = 0;

      rootEl.innerHTML = renderSkillDetailPageShell({
        skill,
        files: currentFiles,
        issues,
        feedback,
        certification,
        currentUserId: getPageUserId(ctx.user),
        currentFileIndex,
        renderRelatedIssuesSection: window.SkillDetailRenderers.renderRelatedIssuesSection,
        renderFlywheelSummary: window.SkillAnalyticsRenderers.renderFlywheelSummary,
      });

      bindSkillDetailCopyActions(skill);
      renderSkillMarkdownContent(skill.skill_md_content);

      const certificationButton = document.getElementById('request-certification-btn') as HTMLButtonElement | null;
      certificationButton?.addEventListener('click', async () => {
        certificationButton.disabled = true;
        const status = document.getElementById('certification-request-status');
        if (status) status.textContent = 'Requesting review...';
        const result = await window.SkillDetailData.requestSkillCertification(skill.id);
        if (!result.ok) {
          certificationButton.disabled = false;
          if (status) status.textContent = result.error;
          return;
        }
        if (status) status.textContent = 'Certification review requested.';
        await loadSkillDetailPage(ctx);
      });

      if (skill.id) {
        loadStarAndReviews(skill.id).catch(function(e) {
          console.warn('Failed to load star/reviews:', e);
          const summaryEl = document.getElementById('rating-summary');
          if (summaryEl) summaryEl.innerHTML = '<span class="text-red-400 text-sm">Failed to load ratings</span>';
          const listEl = document.getElementById('reviews-list');
          if (listEl) listEl.innerHTML = '<p class="text-red-400 text-sm">Failed to load reviews. Please try refreshing.</p>';
        });

        loadSkillAnalytics(skill.id, feedback);
      }
    } catch (error) {
      rootEl.innerHTML = '<div class="card p-6 text-red-300 text-sm">Failed to load skill package: ' +
        escapeHtml((error as Error).message || 'unknown error') +
        '</div>';
    }
  }

  registerSkillReviewHandlers();
  publicPage({ init: loadSkillDetailPage });
})();
